import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  TextField,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  Assessment,
  PlayArrow,
  Business,
  Warning,
  LocationOn,
  CreditCard,
  Schedule,
  FilterList,
  ExpandMore,
  ExpandLess,
  QuestionAnswer,
} from '@mui/icons-material';
import { useCompany } from '../contexts/CompanyContext';
import { useBilling } from '../contexts/BillingContext';
import { auditAPI, AuditSeries, AuditSession, companyAPI, CompanySite, billingAPI, ClientBillingSettings } from '../services/api';
import CompanySelector from '../components/Layout/CompanySelector';
import { supabase } from '../lib/supabaseClient';

const AuditSelection: React.FC = () => {
  const navigate = useNavigate();
  const { companyId } = useParams();
  const { selectedCompany, companies, isLoading: companiesLoading, setSelectedCompany } = useCompany();
  const { isAlwaysFree } = useBilling();
  
  const [auditSeries, setAuditSeries] = useState<AuditSeries[]>([]);
  const [sessions, setSessions] = useState<AuditSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  
  // Site selection modal state
  const [siteModalOpen, setSiteModalOpen] = useState(false);
  const [companySites, setCompanySites] = useState<CompanySite[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [pendingAuditSeriesId, setPendingAuditSeriesId] = useState<number | null>(null);
  const [isLoadingSites, setIsLoadingSites] = useState(false);
  const [isStartingAudit, setIsStartingAudit] = useState(false);
  
  // KPI matching modal state
  const [kpiModalOpen, setKpiModalOpen] = useState(false);
  const [kpiMatches, setKpiMatches] = useState<any[]>([]);
  const [kpiUnmatched, setKpiUnmatched] = useState<any[]>([]);
  const [isLoadingKPIs, setIsLoadingKPIs] = useState(false);
  
  // Client billing state (for showing no credits message)
  const [clientBilling, setClientBilling] = useState<ClientBillingSettings | null>(null);
  const [isLoadingBilling, setIsLoadingBilling] = useState(false);
  
  // Filter state
  const [searchFilter, setSearchFilter] = useState('');
  
  // Expanded descriptions state
  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<number>>(new Set());
  
  // Refs to prevent duplicate API calls
  const loadingAuditDataRef = useRef(false);
  const lastLoadedCompanyRef = useRef<number | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Auto-select company from URL parameter
  useEffect(() => {
    if (companyId && companies.length > 0) {
      const companyIdNum = parseInt(companyId);
      const company = companies.find(c => c.id === companyIdNum);
      if (company && (!selectedCompany || selectedCompany.id !== companyIdNum)) {
        console.log('✅ Auto-selecting company from URL:', company.name);
        setSelectedCompany(company);
      }
    }
  }, [companyId, companies]);

  // Load audit data when selected company changes
  useEffect(() => {
    // Only load if company actually changed (not just re-render)
    if (selectedCompany?.id === lastLoadedCompanyRef.current) {
      return;
    }
    
    // Prevent duplicate calls
    if (loadingAuditDataRef.current) {
      console.log('⏭️ Skipping duplicate loadAuditData call');
      return;
    }
    
    loadAuditData();
  }, [selectedCompany?.id]);

  const loadAuditData = async () => {
    // Prevent duplicate calls
    if (loadingAuditDataRef.current) {
      console.log('⏭️ loadAuditData already in progress, skipping');
      return;
    }
    
    // Cancel any pending requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new abort controller for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    try {
      loadingAuditDataRef.current = true;
      setIsLoading(true);
      setError('');

      console.log('🔄 Loading audit data for company:', selectedCompany?.id || 'none');

      // Load audit series (only once, not company-specific)
      const seriesData = await auditAPI.getAuditSeries();
      
      // Check if request was aborted
      if (abortController.signal.aborted) {
        console.log('⏹️ Request aborted');
        return;
      }
      
      setAuditSeries(seriesData);

      // Load existing sessions if company is selected
      if (selectedCompany) {
        const sessionsData = await auditAPI.getAuditSessions(selectedCompany.id);
        
        // Check if request was aborted
        if (abortController.signal.aborted) {
          console.log('⏹️ Request aborted');
          return;
        }
        
        setSessions(sessionsData);
        lastLoadedCompanyRef.current = selectedCompany.id;
        
        // Load client billing status to check credits (don't await - let it load async)
        loadClientBilling(selectedCompany.id);
      } else {
        setSessions([]);
        setClientBilling(null);
        lastLoadedCompanyRef.current = null;
      }
      
      console.log('✅ Audit data loaded successfully');
    } catch (error: any) {
      // Don't show error if request was aborted
      if (abortController.signal.aborted) {
        return;
      }
      console.error('❌ Error loading audit data:', error);
      setError('Failed to load review data. Please try again.');
    } finally {
      // Only clear loading flag if this is the current request
      if (abortControllerRef.current === abortController) {
        loadingAuditDataRef.current = false;
        setIsLoading(false);
        abortControllerRef.current = null;
      }
    }
  };
  
  // Load client billing status
  const loadClientBilling = async (companyId: number) => {
    try {
      setIsLoadingBilling(true);
      const billing = await billingAPI.getClientBilling(companyId);
      setClientBilling(billing);
    } catch (error: any) {
      console.error('Error loading client billing:', error);
      // Don't show error - billing info is supplementary
      setClientBilling(null);
    } finally {
      setIsLoadingBilling(false);
    }
  };

  // Build audit info with last run status for each audit series, filtered by search
  const auditSeriesWithStatus = useMemo(() => {
    // Apply search filter first
    const filtered = searchFilter.trim() 
      ? auditSeries.filter(series => 
          series.name.toLowerCase().includes(searchFilter.toLowerCase())
        )
      : auditSeries;
    
    return filtered.map(series => {
      // Get all sessions for this audit series
      const seriesSessions = sessions.filter(s => s.audit_series === series.id);
      
      // Find in-progress sessions
      const inProgressSessions = seriesSessions.filter(s => !s.end_date);
      
      // Find the most recent completed session
      const completedSessions = seriesSessions
        .filter(s => s.end_date)
        .sort((a, b) => new Date(b.end_date!).getTime() - new Date(a.end_date!).getTime());
      
      const lastCompleted = completedSessions[0] || null;
      const totalCompleted = completedSessions.length;
      
      return {
        ...series,
        inProgressSessions,
        lastCompleted,
        totalCompleted,
      };
    });
  }, [auditSeries, sessions, searchFilter]);

  // Load company sites and open site selection modal
  const handleStartAuditClick = async (auditSeriesId: number) => {
    if (!selectedCompany) {
      setError('Please select a company first.');
      return;
    }

    try {
      setError('');
      setIsLoadingSites(true);
      setPendingAuditSeriesId(auditSeriesId);
      
      // Load sites for the selected company
      const sites = await companyAPI.getCompanySites(selectedCompany.id);
      setCompanySites(sites);
      
      // Find "Company Level" site and set as default, otherwise use first site
      const companyLevelSite = sites.find((s: CompanySite) => 
        s.name.toLowerCase().includes('company level') || 
        s.name.toLowerCase() === 'company level'
      );
      
      if (companyLevelSite) {
        setSelectedSiteId(companyLevelSite.id);
      } else if (sites.length > 0) {
        setSelectedSiteId(sites[0].id);
      } else {
        setSelectedSiteId(null);
      }
      
      setSiteModalOpen(true);
    } catch (error: any) {
      console.error('Error loading sites:', error);
      setError('Failed to load company sites. Please try again.');
    } finally {
      setIsLoadingSites(false);
    }
  };

  // Load KPI matches and show KPI modal
  const handleProceedToKPIMatching = async () => {
    if (!selectedCompany || !pendingAuditSeriesId) {
      return;
    }
    
    try {
      setIsLoadingKPIs(true);
      setSiteModalOpen(false);
      
      // Call KPI matching API
      const API_BASE = import.meta.env.PROD 
        ? 'https://web-production-d0043.up.railway.app/api'
        : (import.meta.env.VITE_API_URL || 'http://localhost:8001/api');
      
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      
      const response = await fetch(`${API_BASE}/kpis/match/`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({
          template_id: pendingAuditSeriesId,
          company_id: selectedCompany.id
        })
      });
      
      if (!response.ok) throw new Error('Failed to match KPIs');
      
      const data = await response.json();
      setKpiMatches(data.matches || []);
      setKpiUnmatched(data.unmatched || []);
      setKpiModalOpen(true);
    } catch (error: any) {
      console.error('Error matching KPIs:', error);
      setError('Failed to load KPI matches. Proceeding without KPI matching...');
      // Proceed with audit start anyway
      await handleConfirmStartAudit();
    } finally {
      setIsLoadingKPIs(false);
    }
  };

  // Actually start the audit with selected site
  const handleConfirmStartAudit = async () => {
    if (!selectedCompany || !pendingAuditSeriesId || !selectedSiteId) {
      setError('Please select a site to continue.');
      return;
    }

    try {
      setError('');
      setIsStartingAudit(true);
      
      const response = await auditAPI.startAudit({
        company_id: selectedCompany.id,
        site_id: selectedSiteId,
        audit_series_id: pendingAuditSeriesId,
      });

      if (response.session) {
        setSiteModalOpen(false);
        setKpiModalOpen(false);
        navigate(`/audit-session/${response.session.id}`);
      }
    } catch (error: any) {
      console.error('Error starting audit:', error);
      
      // Handle billing-related errors (402 Payment Required)
      if (error.response?.status === 402) {
        const billingError = error.response?.data?.error || 'Payment required to start review';
        setError(billingError);
        setSiteModalOpen(false);
      } else {
        setError(error.response?.data?.error || 'Failed to start review. Please try again.');
      }
    } finally {
      setIsStartingAudit(false);
    }
  };

  // Close site selection modal
  const handleCloseSiteModal = () => {
    setSiteModalOpen(false);
    setPendingAuditSeriesId(null);
    setSelectedSiteId(null);
  };

  const handleResumeAudit = (sessionId: number) => {
    navigate(`/audit-session/${sessionId}`);
  };
  
  // Toggle description expansion
  const toggleDescription = (seriesId: number) => {
    setExpandedDescriptions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(seriesId)) {
        newSet.delete(seriesId);
      } else {
        newSet.add(seriesId);
      }
      return newSet;
    });
  };
  
  // Check if user has no credits available
  const hasNoCredits = () => {
    // If the current user is from an always-free consultant, never show no credits
    if (isAlwaysFree) return false;
    
    if (!clientBilling) return false;
    
    // If client pays, check client credits
    if (clientBilling.billing_responsibility === 'client') {
      const hasCreditsOrTrial = clientBilling.audit_credits_balance > 0 || 
        (clientBilling.trial_days_remaining > 0 && !clientBilling.trial_audit_used);
      return !hasCreditsOrTrial;
    }
    
    return false;
  };
  
  // Check if current user is company_admin for the selected company
  const isCompanyAdmin = () => {
    return selectedCompany?.user_role === 'company_admin';
  };
  
  // Check if client pays for this company
  const clientPays = () => {
    return clientBilling?.billing_responsibility === 'client';
  };

  // Show company selection prompt if no company is selected
  if (!selectedCompany && !companiesLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Select a Company
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Please select a company to begin reviewing
          </Typography>
        </Box>

        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Warning sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            No Company Selected
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            You need to select a company before you can start or view reviews.
          </Typography>
          
          {companies.length > 0 ? (
            <Box sx={{ maxWidth: 400, mx: 'auto' }}>
              <CompanySelector />
            </Box>
          ) : (
            <Alert severity="warning" sx={{ maxWidth: 400, mx: 'auto' }}>
              No companies are available for your account. Please contact your administrator.
            </Alert>
          )}
        </Paper>
      </Container>
    );
  }

  if (isLoading || companiesLoading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Start New Review
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Select a review template to begin a new assessment
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Company Information */}
      {selectedCompany && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <Business sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">
                {selectedCompany.name}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {selectedCompany.city}, {selectedCompany.postcode} • {selectedCompany.employee_count} employees
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* No Credits Alert */}
      {hasNoCredits() && !isLoadingBilling && (
        <Card sx={{ mb: 4, border: '1px solid', borderColor: 'warning.main' }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <CreditCard sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No Review Credits Available
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {clientBilling?.trial_audit_used 
                ? 'Your free trial review has been used. Purchase credits to run more reviews.'
                : 'You need review credits to start a new review.'}
            </Typography>
            {isCompanyAdmin() && clientPays() && (
              <Button
                component={RouterLink}
                to={`/company/${selectedCompany?.id}/settings?tab=billing`}
                variant="contained"
                color="primary"
                startIcon={<CreditCard />}
                sx={{ mt: 1 }}
              >
                Purchase Credits
              </Button>
            )}
            {!isCompanyAdmin() && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Please contact your company administrator to purchase review credits.
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Available Review Templates Table */}
      {!hasNoCredits() && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Assessment color="primary" />
              Available Review Templates ({auditSeriesWithStatus.length})
            </Typography>
            
            {/* Filter */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <FilterList color="action" />
              <TextField
                label="Filter by Review Name"
                variant="outlined"
                size="small"
                placeholder="Search reviews..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                sx={{ minWidth: 300 }}
              />
              {searchFilter && (
                <Typography variant="body2" color="text.secondary">
                  Showing {auditSeriesWithStatus.length} of {auditSeries.length} templates
                </Typography>
              )}
            </Box>
            
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Review Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Last Completed</TableCell>
                    <TableCell>Total Runs</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {auditSeriesWithStatus.map((series) => (
                    <React.Fragment key={series.id}>
                      <TableRow hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Assessment color="action" fontSize="small" />
                              <Typography variant="subtitle2">
                                {series.name}
                              </Typography>
                              {series.description && (
                                <Tooltip title={expandedDescriptions.has(series.id) ? 'Hide description' : 'Show description'}>
                                  <IconButton 
                                    size="small" 
                                    onClick={() => toggleDescription(series.id)}
                                    sx={{ ml: 0.5 }}
                                  >
                                    {expandedDescriptions.has(series.id) ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <QuestionAnswer fontSize="small" sx={{ color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {series.question_count || 0} questions
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                      <TableCell>
                        {series.inProgressSessions.length > 0 ? (
                          <Box>
                            <Chip 
                              label={`In Progress (${series.inProgressSessions.length})`} 
                              color="warning" 
                              size="small" 
                              icon={<Schedule />}
                            />
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                              {series.inProgressSessions.map(s => s.site_name || 'Unknown site').join(', ')}
                            </Typography>
                          </Box>
                        ) : series.totalCompleted > 0 ? (
                          <Chip label="Ready" color="success" size="small" variant="outlined" />
                        ) : (
                          <Chip label="Not Started" color="default" size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>
                        {series.lastCompleted ? (
                          <Box>
                            <Typography variant="body2">
                              {new Date(series.lastCompleted.end_date!).toLocaleDateString()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {series.lastCompleted.site_name || 'Unknown site'}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Never
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {series.totalCompleted}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          {/* Resume buttons for in-progress audits */}
                          {series.inProgressSessions.length > 0 && (
                            series.inProgressSessions.map(session => (
                              <Tooltip key={session.id} title={`Resume: ${session.site_name || 'Unknown site'}`}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  color="warning"
                                  startIcon={<PlayArrow />}
                                  onClick={() => handleResumeAudit(session.id)}
                                >
                                  Resume
                                </Button>
                              </Tooltip>
                            ))
                          )}
                          {/* Start new audit button */}
                          <Tooltip title="Start new review">
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={isLoadingSites && pendingAuditSeriesId === series.id ? 
                                <CircularProgress size={16} color="inherit" /> : 
                                <PlayArrow />
                              }
                              onClick={() => handleStartAuditClick(series.id)}
                              disabled={!selectedCompany || isLoadingSites}
                            >
                              Start New
                            </Button>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                    {/* Collapsible description row */}
                    {series.description && (
                      <TableRow>
                        <TableCell 
                          colSpan={5} 
                          sx={{ py: 0, borderBottom: expandedDescriptions.has(series.id) ? undefined : 0 }}
                        >
                          <Collapse in={expandedDescriptions.has(series.id)} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 2, px: 2, bgcolor: 'action.hover', borderRadius: 1, my: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                {series.description}
                              </Typography>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                    </React.Fragment>
                  ))}
                  {auditSeriesWithStatus.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                          No review templates available
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Site Selection Modal */}
      <Dialog 
        open={siteModalOpen} 
        onClose={handleCloseSiteModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocationOn color="primary" />
          Select Review Scope
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Which site are you reviewing?
          </Typography>
          
          {companySites.length === 0 ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No sites configured for this company. Please add sites in Company Settings before starting a review.
            </Alert>
          ) : (
            <FormControl component="fieldset" sx={{ width: '100%' }}>
              <RadioGroup
                value={selectedSiteId || ''}
                onChange={(e) => setSelectedSiteId(parseInt(e.target.value))}
              >
                {companySites.map((site) => (
                  <FormControlLabel
                    key={site.id}
                    value={site.id}
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body1" fontWeight={site.name.toLowerCase().includes('company level') ? 'bold' : 'normal'}>
                          {site.name}
                        </Typography>
                        {site.name.toLowerCase().includes('company level') && (
                          <Typography variant="caption" color="text.secondary">
                            Review the company as a whole
                          </Typography>
                        )}
                      </Box>
                    }
                    sx={{
                      border: '1px solid',
                      borderColor: selectedSiteId === site.id ? 'primary.main' : 'grey.300',
                      borderRadius: 1,
                      p: 1.5,
                      mb: 1,
                      mr: 0,
                      backgroundColor: selectedSiteId === site.id ? 'primary.50' : 'transparent',
                      '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: 'action.hover',
                      },
                    }}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseSiteModal} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleProceedToKPIMatching}
            disabled={!selectedSiteId || isLoadingKPIs || companySites.length === 0}
            startIcon={isLoadingKPIs ? <CircularProgress size={16} color="inherit" /> : <PlayArrow />}
          >
            {isLoadingKPIs ? 'Loading...' : 'Continue'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* KPI Matching Loading Dialog */}
      <Dialog
        open={isLoadingKPIs}
        disableEscapeKeyDown
        maxWidth="xs"
        fullWidth
      >
        <DialogContent>
          <Box display="flex" flexDirection="column" alignItems="center" py={3}>
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Analyzing KPIs...
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Matching review template KPIs to your company's existing KPIs. This may take a few seconds.
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* KPI Matching Results Dialog */}
      <Dialog
        open={kpiModalOpen}
        onClose={() => setKpiModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          📊 KPI Matching
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This review template influences the following KPIs. We've matched them to your company's existing KPIs:
          </Typography>
          
          {kpiMatches.length === 0 && kpiUnmatched.length === 0 ? (
            <Alert severity="info">
              No KPIs configured for this template yet. You can proceed with the review.
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Template KPI</strong></TableCell>
                    <TableCell><strong>Matched Company KPI</strong></TableCell>
                    <TableCell><strong>Match Quality</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {kpiMatches.map((match, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {match.template_kpi}
                        </Typography>
                        {match.reasoning && (
                          <Typography variant="caption" color="text.secondary">
                            {match.reasoning}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {match.company_kpi_name || 'No match'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={match.confidence || 'N/A'}
                          color={
                            match.confidence === 'high' ? 'success' :
                            match.confidence === 'medium' ? 'warning' : 'default'
                          }
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          
          {/* Show unmatched KPIs if any */}
          {kpiUnmatched.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" fontWeight="bold" color="warning.main" sx={{ mb: 1 }}>
                ⚠ Template KPIs Not Yet in Your System ({kpiUnmatched.length})
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                These KPIs from the review template aren't tracked in your company yet. You can add them to your KPI Dashboard later to measure improvement.
              </Alert>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>KPI Name</strong></TableCell>
                      <TableCell><strong>Why Track This?</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {kpiUnmatched.map((unmatch: any, index: number) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {unmatch.template_kpi}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {unmatch.reasoning || 'Configure in KPI Dashboard after this review'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
          
          <Alert severity="info" sx={{ mt: 2 }}>
            💡 {kpiMatches.length > 0 
              ? `The review will track ${kpiMatches.length} matched KPI(s) to measure improvement over time`
              : 'Add KPIs to your dashboard to track improvement from this review'}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setKpiModalOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmStartAudit}
            disabled={isStartingAudit}
            startIcon={isStartingAudit ? <CircularProgress size={16} color="inherit" /> : <PlayArrow />}
          >
            {isStartingAudit ? 'Starting...' : 'Start Review'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AuditSelection;
